import { useMemo } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { BankingEntitiesPaymentStatusEnum } from 'generated/resources/graphql'

import { dateBackendDateTimeToRelativeTime, dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { VerticalTimeline } from 'modules/banking/components/VerticalTimeline'
import { statusLabelType, statusText } from 'modules/banking/lib/mappers'

type IconColor = 'grey600' | 'viridian800' | 'malibu1000' | 'radicalRed800'

interface PaymentExecution {
  id: number
  status: BankingEntitiesPaymentStatusEnum
  date: string
  executedAt?: string | null
  paidAt?: string | null
  transfersCount: number
}

interface ExecutionsTimelineProps {
  executions: PaymentExecution[]
  onExecutionClick?: (executionId: number) => void
}

const getStatusIconColor = (status: BankingEntitiesPaymentStatusEnum): IconColor => {
  switch (status) {
    case 'paid':
    case 'done':
      return 'viridian800'
    case 'rejected':
    case 'rejected_consent':
      return 'radicalRed800'
    case 'in_progress':
    case 'pending_consent':
      return 'malibu1000'
    case 'init':
    case 'partially_paid':
    default:
      return 'grey600'
  }
}

const ExecutionContent = ({ execution }: { execution: PaymentExecution }) => {
  return (
    <Box flexDirection="column" gap="s4">
      <Text.Normal size="200" color="grey600">
        {dateBackendDateTimeToUI(execution.date)}
      </Text.Normal>
      <Text.Normal size="200" color="grey700">
        {i18n.tp('banking.bulkPayments.detail.transfersCount', { count: execution.transfersCount })}
      </Text.Normal>
    </Box>
  )
}

export const ExecutionsTimeline = ({ executions, onExecutionClick }: ExecutionsTimelineProps) => {
  // Sort by date descending (newest first)
  const sortedExecutions = useMemo(() => {
    return [...executions].sort((a, b) => {
      const dateA = new Date(a.date).getTime()
      const dateB = new Date(b.date).getTime()
      return dateB - dateA
    })
  }, [executions])

  if (sortedExecutions.length === 0) {
    return (
      <Box padding="s16">
        <Text.Normal size="200" color="grey600">
          {i18n.t('banking.bulkPayments.detail.noExecutions')}
        </Text.Normal>
      </Box>
    )
  }

  // Add execution numbers for display
  const executionsWithNumbers = sortedExecutions.map((execution, index) => ({
    ...execution,
    executionNumber: sortedExecutions.length - index,
  }))

  return (
    <Box width="full">
      <VerticalTimeline
        items={executionsWithNumbers}
        getKey={(execution) => execution.id}
        getTitle={(execution) =>
          i18n.t('banking.bulkPayments.detail.executionTitle', {
            number: execution.executionNumber,
          })
        }
        getStatusLabel={(execution) => statusText(execution.status)}
        getStatusType={(execution) => statusLabelType(execution.status)}
        getIconColor={(execution) => getStatusIconColor(execution.status)}
        getRelativeTime={(execution) => dateBackendDateTimeToRelativeTime(execution.date)}
        renderContent={(execution) => <ExecutionContent execution={execution} />}
        onItemClick={onExecutionClick ? (execution) => onExecutionClick(execution.id) : undefined}
      />
    </Box>
  )
}
